import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { WAITLIST_ROLES } from '@/content/site-content';
import { buttonClasses } from '@/components/ui/button-styles';

/**
 * The early-access sign-up.
 *
 * There is no waitlist endpoint yet, and a form that swallows what someone
 * typed would be worse than no form: on submit this hands the details straight
 * to `/register`, which is live, rather than claiming to have stored them.
 * Point `action` at a real endpoint when one exists and drop the navigation.
 */

const STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const FIELD =
  'w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink ' +
  'placeholder:text-ink-soft/70 transition duration-200 focus:border-brand-300';

const LABEL = 'block text-sm font-semibold text-ink';

export function WaitlistSection() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(WAITLIST_ROLES[0]!);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const params = new URLSearchParams({ name: fullName, email, role });
    void navigate(`/register?${params.toString()}`);
  }

  return (
    <section id="waitlist" className="container-page scroll-mt-28 py-16 lg:py-20">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
        <div>
          <h2 className="text-3xl font-extrabold sm:text-4xl lg:text-5xl">
            Join the <span className="text-brand-600">Early Access</span> Waitlist
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ink-soft">
            TeenTack isn&rsquo;t fully live yet - join the waitlist and we&rsquo;ll notify you as
            soon as your role opens up. Founding members get first access and a say in how the
            platform grows.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="waitlist-name" className={LABEL}>
              Full Name
            </label>
            <input
              id="waitlist-name"
              name="name"
              autoComplete="name"
              required
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Enter your full name"
              className={`mt-1.5 ${FIELD}`}
            />
          </div>

          <div>
            <label htmlFor="waitlist-email" className={LABEL}>
              Email address
            </label>
            <input
              id="waitlist-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Enter your email"
              className={`mt-1.5 ${FIELD}`}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="waitlist-state" className={LABEL}>
                State
              </label>
              <select
                id="waitlist-state"
                name="state"
                defaultValue=""
                className={`mt-1.5 ${FIELD}`}
              >
                <option value="" disabled>
                  Choose your state
                </option>
                {STATES.map((state) => (
                  <option key={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="waitlist-city" className={LABEL}>
                City
              </label>
              <input
                id="waitlist-city"
                name="city"
                autoComplete="address-level2"
                placeholder="Enter your city"
                className={`mt-1.5 ${FIELD}`}
              />
            </div>
          </div>

          <div>
            <label htmlFor="waitlist-role" className={LABEL}>
              I&rsquo;m joining as a...
            </label>
            <select
              id="waitlist-role"
              name="role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              className={`mt-1.5 ${FIELD}`}
            >
              {WAITLIST_ROLES.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>

          <button type="submit" className={buttonClasses('primary', 'md', 'w-full')}>
            Join the Waitlist →
          </button>

          <p className="text-xs text-ink-soft">
            We&rsquo;ll only use this to notify you about early access. No spam.
          </p>
        </form>
      </div>
    </section>
  );
}
